/**
 * BajaClinicaModal · PR-FASE-A-3 (Ann 2026-05-27)
 *
 * Modal de acción administrativa consciente para dar de baja a un cliente
 * del circuito clínico/fisioterapia. Llama a la autopista oficial
 * POST /api/admin/clients/{cliente_id}/baja-clinica (PR #242).
 *
 * Decisiones funcionales (Ann):
 *  - Catálogo cerrado de motivos · dropdown con texto natural en español.
 *  - Si motivo='otro' → observaciones obligatorias (validación frontend
 *    + backend como red de seguridad).
 *  - Fecha baja: default hoy · puede ser pasada · NO futura.
 *  - Si 409 TIENE_CITAS_FUTURAS · modal NO cierra · muestra lista
 *    detallada para que admin gestione.
 *  - Si 409 CLIENTE_EN_IS_ACTIVA_CLINICA · modal NO cierra · enlace a IS.
 *  - Tras éxito · cerrar modal + toast verde + onSuccess() (refresco ficha).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { UserX, AlertTriangle, Calendar, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../../api';

// Catálogo cerrado · valor en backend ↔ texto en UI (Ann DF8)
export const MOTIVOS_BAJA_CLINICA = [
  { value: 'alta_medica',       label: 'Alta médica' },
  { value: 'abandono',          label: 'Abandono' },
  { value: 'derivacion_externa',label: 'Derivación externa' },
  { value: 'traslado',          label: 'Traslado' },
  { value: 'decision_cliente',  label: 'Decisión del cliente' },
  { value: 'otro',              label: 'Otro' },
];

const MAX_OBSERVACIONES_LEN = 500;

/**
 * Traduce error backend a mensaje usuario. Defensivo contra cambios de
 * formato `error.response.data.detail` (envoltura `{code, detail}`).
 */
export function extractErrorPayload(error) {
  if (!error?.response?.data) {
    return { code: null, detail: null, status: null };
  }
  const status = error.response.status;
  const data = error.response.data;
  // Envoltura habitual de los endpoints: {detail: {code, detail}}
  const wrap = data.detail;
  if (wrap && typeof wrap === 'object' && wrap.code) {
    return { code: wrap.code, detail: wrap.detail, status };
  }
  // FastAPI directo: {detail: "..."}
  if (typeof data.detail === 'string') {
    return { code: null, detail: data.detail, status };
  }
  return { code: null, detail: data, status };
}

export function traducirErrorBaja(error) {
  const { code, status } = extractErrorPayload(error);
  if (status === 403) return 'No tienes permiso para dar de baja en fisioterapia.';
  if (!error?.response) return 'Sin conexión con el servidor. Inténtalo de nuevo.';
  const map = {
    CLIENTE_NO_EXISTE:        'Cliente no encontrado. Recarga la página.',
    NO_TIENE_FISIO_ACTIVO:    'Este cliente no tiene fisioterapia activa. Recarga la ficha.',
    MOTIVO_OBLIGATORIO:       'Selecciona un motivo.',
    MOTIVO_INVALIDO:          'Motivo no válido. Recarga la página.',
    OBSERVACIONES_OBLIGATORIAS_SI_OTRO: "Las observaciones son obligatorias cuando el motivo es 'Otro'.",
    OBSERVACIONES_DEMASIADO_LARGAS: `Observaciones demasiado largas (máximo ${MAX_OBSERVACIONES_LEN} caracteres).`,
    FECHA_INVALIDA:           'Fecha inválida. Usa el selector de fecha.',
    POSTCONDICION_FALLIDA:    'Error inesperado en la operación. Datos no modificados. Contacta con el administrador.',
    EXCEPCION_NO_CONTROLADA:  'Error interno del servidor. Datos no modificados. Contacta con el administrador.',
  };
  return map[code] || 'Error al dar de baja. Inténtalo de nuevo.';
}

const HOY_ISO = () => new Date().toISOString().split('T')[0];

const BajaClinicaModal = ({ isOpen, onClose, clientId, clientName, onSuccess }) => {
  const [fechaBaja, setFechaBaja] = useState(HOY_ISO());
  const [motivo, setMotivo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Errores tipados que requieren panel inline (modal NO cierra):
  // - TIENE_CITAS_FUTURAS → { type: 'citas_futuras', citas: [...] }
  // - CLIENTE_EN_IS_ACTIVA_CLINICA → { type: 'is_activa', detail: {...} }
  const [errorInline, setErrorInline] = useState(null);

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setFechaBaja(HOY_ISO());
      setMotivo('');
      setObservaciones('');
      setSubmitting(false);
      setErrorInline(null);
    }
  }, [isOpen]);

  const observacionesObligatorias = motivo === 'otro';
  const observacionesValidas = !observacionesObligatorias || observaciones.trim().length > 0;
  const observacionesLongitudOk = observaciones.length <= MAX_OBSERVACIONES_LEN;
  const fechaValida = fechaBaja && fechaBaja <= HOY_ISO();
  const motivoValido = motivo && MOTIVOS_BAJA_CLINICA.some((m) => m.value === motivo);

  const canSubmit = !submitting && motivoValido && observacionesValidas && observacionesLongitudOk && fechaValida;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorInline(null);

    const payload = {
      fecha_baja_clinica: fechaBaja,
      motivo_baja_clinica: motivo,
    };
    if (observaciones.trim()) {
      payload.observaciones_baja_clinica = observaciones.trim();
    }

    try {
      const res = await axios.post(`${API}/admin/clients/${clientId}/baja-clinica`, payload);
      // FEATURE BAJA PROGRAMADA · fecha futura → el backend PROGRAMA la baja
      // (el paciente sigue DE ALTA hasta esa fecha; el automatismo la ejecuta).
      if (res.data?.via_autopista === 'baja_programada') {
        toast.success(
          `Baja clínica PROGRAMADA para el ${res.data.baja_programada?.fecha_baja || fechaBaja}. ` +
          'El paciente sigue DE ALTA hasta esa fecha.'
        );
      } else {
        toast.success(`Cliente dado de baja del circuito clínico · ${fechaBaja}`);
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      const { code, detail } = extractErrorPayload(error);
      if (code === 'TIENE_CITAS_FUTURAS') {
        // detail = { mensaje, total, citas_futuras }
        const citas = (detail && detail.citas_futuras) || [];
        setErrorInline({ type: 'citas_futuras', citas, total: citas.length });
      } else if (code === 'CLIENTE_EN_IS_ACTIVA_CLINICA') {
        setErrorInline({ type: 'is_activa' });
      } else {
        toast.error(traducirErrorBaja(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const irACuadrante = (fecha) => {
    // Best-effort: si no existe la ruta exacta, abre cuadrante sin fecha.
    try {
      const target = `/admin?tab=cuadrante&fecha=${encodeURIComponent(fecha || '')}`;
      window.open(target, '_blank', 'noopener');
    } catch (e) {
      // sin acción · admin verá la cita en pantalla
    }
  };

  const irAIncidencias = () => {
    try {
      const target = `/admin?tab=incidencias-salud&cliente_id=${encodeURIComponent(clientId || '')}`;
      window.open(target, '_blank', 'noopener');
    } catch (e) {
      // sin acción
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <UserX className="w-5 h-5" />
            Dar de baja de fisioterapia
            {clientName && <span className="text-gray-600 font-normal">· {clientName}</span>}
          </DialogTitle>
          <DialogDescription>
            Esta acción cierra el circuito clínico del cliente. No afecta a clases fijas
            ni a otros servicios.
          </DialogDescription>
        </DialogHeader>

        {/* Panel inline · TIENE_CITAS_FUTURAS */}
        {errorInline?.type === 'citas_futuras' && (
          <div className="border border-red-300 bg-red-50 rounded p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-red-800 mb-2">
              <AlertTriangle className="w-4 h-4" /> No se puede dar de baja
            </div>
            <div className="text-red-900 mb-2">
              El cliente tiene {errorInline.total} cita{errorInline.total !== 1 ? 's' : ''}/sesión
              {errorInline.total !== 1 ? 'es' : ''} de bono futura{errorInline.total !== 1 ? 's' : ''} abierta{errorInline.total !== 1 ? 's' : ''}.
              Cancela o reprograma estas citas antes de continuar:
            </div>
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {errorInline.citas.map((c) => (
                <li key={c._id} className="bg-white border border-red-200 rounded p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm">
                      <div className="flex items-center gap-1 text-gray-900 font-medium">
                        <Calendar className="w-3.5 h-3.5" />
                        {c.fecha} {c.hora_inicio}
                        {c.hora_fin ? `–${c.hora_fin}` : ''}
                      </div>
                      <div className="text-gray-700">
                        {c.profesional_nombre || 'Profesional no especificado'}
                        {c.tratamiento_nombre ? ` · ${c.tratamiento_nombre}` : ''}
                        {c.bono_id ? ' · Sesión de bono' : ''}
                        {c.sede ? ` · ${c.sede}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => irACuadrante(c.fecha)}
                      className="text-blue-600 hover:underline text-xs flex items-center gap-0.5 shrink-0"
                    >
                      Ver en cuadrante <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setErrorInline(null)}>
                Volver
              </Button>
              <Button variant="ghost" size="sm" onClick={handleClose}>Cerrar</Button>
            </div>
          </div>
        )}

        {/* Panel inline · CLIENTE_EN_IS_ACTIVA_CLINICA */}
        {errorInline?.type === 'is_activa' && (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-amber-800 mb-2">
              <AlertTriangle className="w-4 h-4" /> Acción bloqueada
            </div>
            <div className="text-amber-900">
              El cliente tiene una incidencia de salud / pausa clínica activa.
              Resuelve o cierra la pausa clínica en la pestaña Incidencias de
              Salud antes de continuar.
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={irAIncidencias}>
                Ir a Incidencias de Salud <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleClose}>Cerrar</Button>
            </div>
          </div>
        )}

        {/* Formulario · solo si no hay panel de error inline */}
        {!errorInline && (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="fecha-baja-clinica">Fecha de baja</Label>
              <Input
                id="fecha-baja-clinica"
                type="date"
                value={fechaBaja}
                max={HOY_ISO()}
                onChange={(e) => setFechaBaja(e.target.value)}
                disabled={submitting}
              />
              {!fechaValida && fechaBaja && (
                <p className="text-xs text-red-600">La fecha no puede ser futura.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="motivo-baja-clinica">Motivo *</Label>
              <select
                id="motivo-baja-clinica"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                disabled={submitting}
              >
                <option value="">Selecciona un motivo</option>
                {MOTIVOS_BAJA_CLINICA.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="observaciones-baja-clinica">
                Observaciones {observacionesObligatorias ? '*' : '(opcional)'}
              </Label>
              <Textarea
                id="observaciones-baja-clinica"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                disabled={submitting}
                rows={3}
                placeholder={observacionesObligatorias
                  ? "Obligatorio cuando el motivo es 'Otro'"
                  : 'Información adicional (opcional)'}
              />
              <div className="flex justify-between text-xs">
                {observacionesObligatorias && observaciones.trim().length === 0 ? (
                  <span className="text-red-600">Obligatorio para motivo &apos;Otro&apos;</span>
                ) : <span />}
                <span className={observacionesLongitudOk ? 'text-gray-500' : 'text-red-600'}>
                  {observaciones.length}/{MAX_OBSERVACIONES_LEN}
                </span>
              </div>
            </div>
          </div>
        )}

        {!errorInline && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Procesando…</>
              ) : (
                <><UserX className="w-4 h-4 mr-1" /> Dar de baja</>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BajaClinicaModal;
